import React, { useState, useContext, useEffect } from 'react';
import { Button as MuiButton } from '@mui/material';

import { ShipyardContext, UserStateContext } from '../Context';
import './Button.scss';

/**Stylized button
 * @param {Object} props Incoming JSX attributes.
 * @param {"primary"|"secondary"|"tertiary"|"negative"|"grey"} props.type Controls the styles and functionality applied to the button.
 * @param {boolean} props.disabled Controls whether or not the button is in a disabled state.
 * @param {string} props.defaultText When present, will override whatever children are passed. When not present, the children will be used as the default text.
 * @param {boolean} props.interactive Whether or not primary button types have an interactive clicked state. Defaults to true, but can be overridden by passing false.
 * @param {string} props.interactiveText A string value that will be prepended to ticking ellipses on interactive primary buttons when the button is in a clicked state.
 * @param {string} props.className A string value that will be prepended to the default class values when the button is generated.
 * @param {Function} props.onClick A function that is called directly in the click handler of the generated HTML button element.
 * @returns {JSX.Element} Stylized button
 */
function Button(props) {
    //Context
    const { doingAjax } = useContext(UserStateContext);
    const { userSettings } = useContext(ShipyardContext);

    //Props and States
    let buttonType;
    let themePalette;
    switch (props.type) {
        case 'negative':
            buttonType = 'negative';
            themePalette = 'error';
            break;
        case 'secondary':
            themePalette = buttonType = 'secondary';
            break;
        case 'tertiary':
            buttonType = 'tertiary';
            break;
        case 'grey':
            themePalette = buttonType = 'grey';
            break;
        case 'primary':
        default:
            themePalette = buttonType = 'primary';
            break;
    }
    const [interactive] = useState(buttonType === 'primary' && props?.interactive !== false ? true : false);
    const [disabled, setDisabled] = useState(doingAjax ? doingAjax : typeof props?.disabled === 'boolean' ? props.disabled : false);
    const [clicked, setClicked] = useState(false);
    const [text, setText] = useState(props.defaultText ? props.defaultText : props.children);
    const [interactiveText] = useState(props.interactiveText ? props.interactiveText : text);
    const [dots, setDots] = useState('.');

    //Events
    let handleClick = (event) => {
        setClicked(true);
        if (typeof props.onClick === 'function') props.onClick();
    };

    //Effects
    useEffect(() => {
        setDisabled(doingAjax || props.disabled);
        if (!doingAjax) setClicked(false);
    }, [doingAjax, props.disabled]);

    useEffect(() => {
        let myInterval =
            doingAjax && interactive && clicked
                ? setInterval(() => {
                      dots.length < 3 ? setDots(dots + '.') : setDots('.');
                  }, 1000)
                : setDots('.');

        return () => {
            clearInterval(myInterval);
        };
    }, [doingAjax, interactive, clicked, dots]);

    useEffect(() => {
        setText(props.defaultText ? props.defaultText : props.children);
    }, [userSettings.LanguagePreference, props.defaultText, props.children]);

    //JSX
    return props.useMui === true ? (
        <MuiButton
            className={(props.className ? props.className + ' ' : '') + buttonType}
            onClick={handleClick}
            disabled={disabled}
            variant={props.variant}
            color={themePalette}
            sx={{
                bgColor: `${themePalette}.${props.colorType}`,
            }}
        >
            {interactive && clicked ? interactiveText + dots : text}
        </MuiButton>
    ) : (
        <div className={'button-container' + (typeof props?.className === 'string' ? ' ' + props.className + '-container' : '')}>
            <button
                className={(props.className ? props.className + ' ' : '') + buttonType}
                onClick={handleClick}
                disabled={disabled}
            >
                {interactive && clicked ? interactiveText + dots : text}
            </button>
        </div>
    );
}

Button.defaultProps = {
    useMui: false,
    variant: 'contained',
    type: 'primary',
    subType: 'main',
};

export default Button;
